import { InboxOutlined } from '@ant-design/icons';
import { Col, Form, Modal, Row } from 'antd';
import Upload, { UploadChangeParam, UploadFile } from 'antd/es/upload';
import Dragger from 'antd/es/upload/Dragger';
import { companySetup } from 'Api/companySetup';
import { employeeApi } from 'Api/employee';
import { leaveRequestApi } from 'Api/leaveRequest';
import { leaveManagementApi } from 'Api/masters/leave-management';
import Buttons from 'components/Global/Buttons';
import DatePickerField from 'components/Global/DatePicker';
import DatePickerFieldForLeave from 'components/Global/DatePickerForLeave';
import InputField from 'components/Global/InputField';
import SelectDropdown from 'components/Global/SelectDropdown';
import TextareaFieldInput from 'components/Global/textareaField';
import dayjs from 'dayjs';
import React, { useEffect, useState } from 'react';
import {
	formatToDateOnly,
	getFinancialYearRange,
	hasFormError,
	toastText,
	validateFormData,
} from 'utils/utils';
import styles from './index.module.scss';
import './index.scss';
import { AddLeaveRequestProps } from './type';

interface DropdownOption {
	label: string;
	value: string;
}

const AddLeaveRequestModal: React.FC<AddLeaveRequestProps> = (props) => {
	const {
		handleCancel,
		isAddLeaveRequestModalOpen,
		fetchLeaveRequest,
		edit,
		setEdit,
	} = props;

	const [employeeMaxLeaves, setEmployeeMaxLeaves] = useState(0);
	const [financialMonth, setFinancialMonth] = useState<number | null>(null);
	const [financialMonthLoading, setFinancialMonthLoading] =
		useState<boolean>(false);

	const [leaveRequestDetails, setLeaveRequestDetails] = useState<any>({
		year: dayjs(),
		employeeId: null,
		FinancialYearRange: null,
		startDate: null,
		endDate: null,
		reason: null,
		leaveId: null,
	});

	const [formError, setFormError] = useState<any>({
		year: false,
		employeeId: false,
		startDate: false,
		FinancialYearRange: false,
		endDate: false,
		reason: false,
		leaveId: false,
	});

	const [file, setFile] = useState<any>(null);
	const [fileList, setFileList] = useState<any>([]);
	const [isLoading, setIsLoading] = useState(false);
	const [leaveDays, setLeaveDays] = useState<number>(0);
	const [leaveTypeDropdown, setLeaveTypeDropdown] = useState<any>([]);
	const [leaveLoading, setLeaveLoading] = useState(false);
	const [maxLeaves, setMaxLeaves] = useState(0);
	const [attachmentRequired, setAttachmentRequired] = useState(false);
	const [fetchedFile, setFetchedFile] = useState<any>(null);
	const [appliedLeaves, setAppliedLeaves] = useState<any>([]);

	const [employeeOptions, setEmployeeOptions] = useState<DropdownOption[]>(
		[]
	);
	const [employeeOptionsLoading, setEmployeeOptionsLoading] =
		useState<boolean>(false);

	const isDateOverlapping = (
		startDate: string,
		endDate: string,
		appliedLeaves: any
	) => {
		const start = dayjs(startDate).startOf('day');
		const end = dayjs(endDate).startOf('day');

		const overlappingLeaves = appliedLeaves.filter((leave: any) => {
			const leaveStartDate = dayjs(leave.startDate).startOf('day');
			const leaveEndDate = dayjs(leave.endDate).startOf('day');
			return (
				start.isSame(leaveStartDate) ||
				start.isSame(leaveEndDate) ||
				(start.isAfter(leaveStartDate) &&
					start.isBefore(leaveEndDate)) ||
				end.isSame(leaveStartDate) ||
				end.isSame(leaveEndDate) ||
				(end.isAfter(leaveStartDate) && end.isBefore(leaveEndDate)) ||
				(start.isBefore(leaveStartDate) && end.isAfter(leaveEndDate))
			);
		});

		const isHolidayOverlap = overlappingLeaves.length > 0;

		if (isHolidayOverlap) {
			const overlappingDates = overlappingLeaves
				.map(
					(leave: any) =>
						`${dayjs(leave.startDate).format(
							'DD-MM-YYYY'
						)} to ${dayjs(leave.endDate).format('DD-MM-YYYY')}`
				)
				.join(', ');

			toastText(
				`Leave dates overlap with existing leaves (${overlappingDates}). Please choose different dates`,
				'error'
			);
			return true;
		}

		return false;
	};

	const validateSubmission = () => {
		const checkFormError = validateFormData(
			{ ...leaveRequestDetails },
			{ ...formError }
		);
		setFormError(checkFormError);

		if (
			isDateOverlapping(
				leaveRequestDetails.startDate,
				leaveRequestDetails.endDate,
				appliedLeaves
			)
		) {
			return { valid: false, error: 'overlap' };
		}

		if (hasFormError(checkFormError)) {
			return { valid: false, error: 'form' };
		}

		if (attachmentRequired && !file && !edit) {
			return { valid: false, error: 'attachment' };
		}

		return { valid: true };
	};

	const createLeaveFormData = () => {
		const leaveFormData = new FormData();
		leaveFormData.append('employeeId', leaveRequestDetails.employeeId);
		leaveFormData.append(
			'startDate',
			formatToDateOnly(leaveRequestDetails.startDate)
		);
		leaveFormData.append(
			'endDate',
			formatToDateOnly(leaveRequestDetails.endDate)
		);
		leaveFormData.append('reason', leaveRequestDetails.reason);
		leaveFormData.append('leaveId', leaveRequestDetails.leaveId);
		leaveFormData.append('noOfDays', leaveDays as any);
		leaveFormData.append('employeeMaxLeaves', employeeMaxLeaves as any);
		leaveFormData.append(
			'year',
			dayjs(leaveRequestDetails.year).format('YYYY')
		);
		leaveFormData.append('moduleName', 'LEAVE_REQUEST');
		if (edit) {
			if (
				fileList[0] &&
				(!fetchedFile || fileList[0].name !== fetchedFile.name)
			) {
				leaveFormData.append('file', fileList[0]);
			}
		} else {
			leaveFormData.append('file', file);
		}
		return leaveFormData;
	};

	const handleSubmit = async () => {
		const validation = validateSubmission();
		if (!validation.valid) {
			if (validation.error === 'attachment') {
				toastText('Attachment is required', 'error');
			}
			return;
		}

		const leaveFormData = createLeaveFormData();
		setIsLoading(true);
		try {
			if (edit) {
				await leaveRequestApi.updateLeaveRequest(
					edit.id,
					leaveFormData
				);
			} else {
				await leaveRequestApi.createLeaveRequest(leaveFormData);
			}

			fetchLeaveRequest();
			let message = edit
				? 'Leave Request has been updated successfully'
				: 'Leave Request has been requested successfully';

			toastText(message, 'success');
			closeModal();
		} catch (err: any) {
			let message;
			if (err.response?.data?.message) {
				message = err.response.data.message;
			} else {
				message = `Something went wrong in ${
					edit ? 'Updating' : 'Creating'
				} Leave Request`;
			}

			toastText(message, 'error');
		}
		setIsLoading(false);
	};

	const closeModal = () => {
		setLeaveRequestDetails({
			year: null,
			employeeId: null,
			startDate: null,
			endDate: null,
			reason: null,
			leaveId: null,
		});
		setEmployeeMaxLeaves(0);
		setLeaveTypeDropdown([]);
		setFormError({
			year: false,
			employeeId: false,
			startDate: false,
			endDate: false,
			reason: false,
			leaveId: false,
		});
		setEdit(null);
		setFile(null);
		setLeaveDays(0);
		setFileList([]);
		setMaxLeaves(0);
		setAttachmentRequired(false);
		handleCancel();
	};

	const fetchLeaveType = async (employeeId: string, year: string) => {
		try {
			setLeaveLoading(true);
			const response = await leaveManagementApi.getLeaveTypeByEmployeeId(
				employeeId,
				year
			);

			setLeaveTypeDropdown(response?.data?.data.data);
			let _appliedLeaves = response?.data?.data.appliedLeaves;
			if (edit) {
				_appliedLeaves = _appliedLeaves.filter(
					(leave: any) => leave.id !== edit.id
				);
			}
			setAppliedLeaves(_appliedLeaves);
		} catch (err: any) {
		} finally {
			setLeaveLoading(false);
		}
	};

	const countNoOfDays = (startDate: any, endDate: any) => {
		if (startDate && endDate) {
			const _startDate = new Date(startDate);
			const _endDate = new Date(endDate);
			_startDate.setHours(0, 0, 0, 0);
			_endDate.setHours(0, 0, 0, 0);

			let count = 0;
			const currentDate = new Date(_startDate);

			while (currentDate <= _endDate) {
				// Skip Sunday (0 is Sunday in getDay())
				if (currentDate.getDay() !== 0) {
					count++;
				}
				currentDate.setDate(currentDate.getDate() + 1);
			}

			setLeaveDays(count);
			return count;
		} else {
			setLeaveDays(0);
			return 0;
		}
	};

	const handleChange = (
		value: string | number | null,
		name: string,
		required: boolean,
		regex?: RegExp | null
	) => {
		if (name === 'year') {
			const fiscalMonth = financialMonth;
			const startDateOfFiscalMonth = dayjs()
				.year(dayjs(value).year())
				.month(fiscalMonth ?? 0)
				.startOf('month');

			const _FinancialYearRange = getFinancialYearRange(
				dayjs(value).year(),
				fiscalMonth ?? 0
			);

			setLeaveRequestDetails((prev: any) => {
				const newDetails = {
					...prev,
					year: dayjs(value).format('YYYY'),
					startDate: startDateOfFiscalMonth,
					endDate: '',
					FinancialYearRange: _FinancialYearRange,
					leaveId: '',
				};
				return newDetails;
			});
			if (leaveRequestDetails.employeeId && value) {
				fetchLeaveType(
					leaveRequestDetails.employeeId,
					dayjs(value).format('YYYY')
				);
			}
		}
		if (name === 'employeeId') {
			setLeaveTypeDropdown([]);
			setLeaveRequestDetails((prev: any) => {
				const newDetails = {
					...prev,
					leaveId: '',
					startDate: '',
					endDate: '',
				};
				return newDetails;
			});

			if (value && leaveRequestDetails.year) {
				fetchLeaveType(
					value as string,
					dayjs(leaveRequestDetails.year).format('YYYY')
				);
			}
		}

		if (name === 'startDate') {
			const leaveId = leaveRequestDetails.leaveId;
			const day = dayjs(value).day();
			const addDays =
				day === 6
					? Math.max(0, maxLeaves)
					: Math.max(0, maxLeaves - 1);
			if (leaveId !== 'LWP') {
				setLeaveRequestDetails((prev: any) => {
					const newDetails = {
						...prev,
						endDate: dayjs(value).add(addDays, 'days'),
					};
					return newDetails;
				});
			} else {
				setLeaveRequestDetails((prev: any) => {
					const newDetails = {
						...prev,
						endDate: null,
					};
					return newDetails;
				});
			}

			if (leaveId === 'HALF_DAY') {
				setLeaveDays(0.5);
				setLeaveRequestDetails((prev: any) => {
					const newDetails = {
						...prev,
						endDate: value,
					};
					return newDetails;
				});
			}
		}

		if (name === 'leaveId') {
			const leaveType = leaveTypeDropdown.find(
				(leave: any) => leave.value === value
			);
			setMaxLeaves(leaveType?.totalLeaves ?? 0);
			setEmployeeMaxLeaves(leaveType?.maxLeaves || 0);
			setAttachmentRequired(leaveType?.attachmentRequired || false);
			setLeaveDays(0);
			setLeaveRequestDetails((prev: any) => {
				const newDetails = {
					...prev,
					startDate: '',
					endDate: '',
				};
				return newDetails;
			});
		}
		OnChange(value, name);
	};

	const OnChange = (value: string | number | null, key: string) => {
		setLeaveRequestDetails((prev: any) => {
			const newDetails = {
				...prev,
				[key]: value,
			};
			if (key === 'startDate' || key === 'endDate') {
				if (leaveRequestDetails.leaveId === 'HALF_DAY') {
					setLeaveDays(0.5);
				} else countNoOfDays(newDetails.startDate, newDetails.endDate);
			}
			return newDetails;
		});

		const checkFormError = validateFormData(
			{ [key]: value },
			{ ...formError }
		);
		setFormError(checkFormError);
	};
	let isRemoving = false;

	const propsUpload = {
		name: 'file',
		maxCount: 1,
		fileList: fileList,
		beforeUpload: (file: UploadFile) => {
			if (!file) {
				return;
			}

			const isLt1M = file.size! / 1024 / 1024 < 5;
			if (!isLt1M) {
				toastText('File must be smaller than 5MB', 'error');
				return Upload.LIST_IGNORE;
			}

			setFile(file);
			setFileList([file]);
			return false;
		},
		onChange(info: UploadChangeParam<UploadFile>) {
			const { file } = info;
			if (!isRemoving) {
				setFile(file);
				setFileList([file]);
			} else {
				isRemoving = false;
				setFile(null);
				setFileList([]);
			}
		},
		onDrop: () => {
			setFile(null);
			setFileList([]);
		},
		onRemove: () => {
			isRemoving = true;
		},
	};
	const CustomDragger = (
		<Dragger {...propsUpload} className="test">
			<p className="ant-upload-drag-icon">
				<InboxOutlined />
			</p>
			<p className="ant-upload-hint">
				Drag and drop a file or choose file from Device.
				<br />{' '}
				<span className="color-purple">Maximum file size : 5MB</span>
			</p>
		</Dragger>
	);

	const myButtons = [
		{
			text: 'Cancel',
			isLoading: false,
			className: 'secondary-button',
			fontSize: '1.8rem',
			minWidth: '12rem',
			minHeight: '4rem',
			disabled: isLoading,
			isSubmit: false,
			onclick: () => {
				closeModal();
			},
		},
		{
			text: 'Save',
			isLoading: isLoading,
			className: 'primary-button',
			fontSize: '1.8rem',
			minWidth: '12rem',
			minHeight: '4rem',
			isSubmit: true,
			disabled: leaveLoading,
			onclick: () => {
				handleSubmit();
			},
		},
	];

	const fetchFinancialYear = async () => {
		try {
			setFinancialMonthLoading(true);
			const financialYear = await companySetup.getFinancialYear();
			const _month = financialYear.data.data;
			setFinancialMonth(_month - 1);
			const financialStartMonth =
				_month >= 1 && _month <= 12 ? _month - 1 : 0;

			const _FinancialYearRange = getFinancialYearRange(
				dayjs().year(),
				financialStartMonth
			);
			setLeaveRequestDetails((prev: any) => {
				const newDetails = {
					...prev,
					FinancialYearRange: _FinancialYearRange,
				};
				return newDetails;
			});
		} catch (error: any) {
			toastText(
				error?.response?.data?.message ||
					'Something went wrong in fetching financial year',
				'error'
			);
		} finally {
			setFinancialMonthLoading(false);
		}
	};

	useEffect(() => {
		fetchFinancialYear();
	}, []);

	useEffect(() => {
		if (edit) {
			const dateRange = getFinancialYearRange(
				edit.year,
				financialMonth ?? 0
			);
			const editData = {
				startDate: dayjs(edit.startDate),
				endDate: dayjs(edit.endDate),
				reason: edit.reason,
				status: edit.status,
				employeeId: edit?.employeeId,
				leaveId: edit.leaveId,
				FinancialYearRange: dateRange,
				noOfDays: edit.noOfDays,
				year: dayjs().year(edit.year),
			};

			if (edit.documentName) {
				const file = {
					name: edit.documentName,
				};
				setFileList([file]);
				setFetchedFile(file);
			} else {
				setFileList([]);
				setFetchedFile(null);
			}

			setLeaveRequestDetails(editData);
			fetchLeaveType(edit.employeeId, edit.year);

			if (edit.leaveId === 'HALF_DAY') {
				setLeaveDays(0.5);
			} else countNoOfDays(editData.startDate, editData.endDate);
		}
	}, [edit]);

	useEffect(() => {
		if (edit && leaveTypeDropdown.length > 0) {
			const _maxLeaves = leaveTypeDropdown.find(
				(leave: any) => leave.value === edit.leaveId
			);
			setMaxLeaves(_maxLeaves?.totalLeaves ?? 0);
			setEmployeeMaxLeaves(_maxLeaves?.maxLeaves);
			setAttachmentRequired(_maxLeaves?.attachmentRequired);
		}
	}, [leaveTypeDropdown, edit]);

	const fetchEmployeeOptions = async () => {
		try {
			setEmployeeOptionsLoading(true);
			const response = await employeeApi.fetchBranchWiseEmployees();
			const employeeOptions: DropdownOption[] = response.data.data;
			setEmployeeOptions(employeeOptions);
		} catch (error: any) {
			toastText(
				error?.response?.data?.message ||
					'Something went wrong in fetching financial year',
				'error'
			);
		} finally {
			setEmployeeOptionsLoading(false);
		}
	};

	useEffect(() => {
		fetchEmployeeOptions();
	}, []);

	return (
		<Modal
			open={isAddLeaveRequestModalOpen}
			onOk={handleSubmit}
			onCancel={closeModal}
			okText={'Save'}
			closable={false}
			width={800}
			maskClosable={false}
			footer={null}
			rootClassName="addLeaveRequestModalRoot"
			title={<h2>{edit ? 'Edit Leave Request' : 'Add Leave Request'}</h2>}
			className="addLeaveRequestModal"
		>
			<p className={styles['form-container-head-warning']}>
				<b>
					{' '}
					<sup>*</sup>
				</b>{' '}
				Indicated mandatory fields
			</p>
			<div className={styles['leave-scrollable-content']}>
				<Row className={styles['form-container-card']} gutter={50}>
					<Col span={12} className={`${styles['col']}`}>
						<DatePickerField
							onChange={(value) => {
								handleChange(value, 'year', true);
							}}
							value={leaveRequestDetails.year}
							isError={formError.year}
							disabledBeforeDates={dayjs().startOf('day')}
							placeholder="Select a year"
							label="Select a year"
							required
							name="year"
							helperText="Select the year"
							picker="year"
							disabled={financialMonthLoading}
						/>
					</Col>
					<Col span={12} className={`${styles['col']}`}>
						<InputField
							name="financialYear"
							value={
								leaveRequestDetails.year
									? leaveRequestDetails.FinancialYearRange
									: 'Loading...'
							}
							label="Financial Year"
							required={false}
							helperText="Pay Period is required"
							onChange={(value) => {}}
							placeholder="Select a financial year"
							isError={false}
							disabled={true}
						/>
					</Col>

					<Col span={12} className={`${styles['col']} margin-top-10`}>
						<SelectDropdown
							placeholder="Employee Name"
							options={employeeOptions}
							value={leaveRequestDetails?.employeeId}
							onChange={(value: any) =>
								handleChange(value, 'employeeId', true)
							}
							size="large"
							required={true}
							helperText="Employee Name required"
							label="Employee Code | Employee Name"
							disabled={employeeOptionsLoading}
							loading={employeeOptionsLoading}
							isError={formError.employeeId}
						/>
					</Col>
					<Col span={12} className={`${styles['col']} margin-top-10`}>
						<SelectDropdown
							placeholder="Leave Type"
							options={leaveTypeDropdown}
							value={leaveRequestDetails?.leaveId}
							disabled={leaveLoading}
							onChange={(value: any) =>
								handleChange(value, 'leaveId', true)
							}
							size="large"
							required={true}
							helperText="Leave Type required"
							label="Leave Type"
							isError={formError.leaveId}
							loading={leaveLoading}
						/>
					</Col>
					<Col span={12} className={`${styles['col']} margin-top-10`}>
						<DatePickerFieldForLeave
							name="startDate"
							onChange={(value) => {
								handleChange(value, 'startDate', true);
							}}
							value={leaveRequestDetails.startDate}
							isError={formError.startDate}
							disabled={leaveLoading}
							disableSunday={true}
							required={true}
							helperText=" Start Date required"
							label="Start Date"
							disabledBeforeDates={dayjs(
								`${dayjs(leaveRequestDetails.year).year()}-${
									financialMonth ? financialMonth + 1 : 1
								}`
							).startOf('day')}
							disabledAfterDates={dayjs(
								`${
									dayjs(leaveRequestDetails.year).year() + 1
								}-${financialMonth ?? 1}`
							).endOf('month')}
							placeholder="Select Start Date"
						/>
					</Col>
					<Col span={12} className={`${styles['col']} margin-top-10`}>
						<DatePickerFieldForLeave
							name="endDate"
							onChange={(value) => {
								handleChange(value, 'endDate', true);
							}}
							value={leaveRequestDetails.endDate}
							isError={formError.endDate}
							disableSunday={true}
							required={true}
							label="End Date"
							helperText="End Date required"
							placeholder="Select End Date"
							disabled={
								!leaveRequestDetails.startDate ||
								leaveLoading ||
								leaveRequestDetails.leaveId === 'HALF_DAY'
							}
							disabledBeforeDates={leaveRequestDetails.startDate}
							disabledAfterDates={
								maxLeaves !== -1 && maxLeaves > 0
									? maxLeaves
									: undefined
							}
						/>
					</Col>
					<Col span={12} className={`${styles['col']} margin-top-10`}>
						<InputField
							name="noOfDays"
							value={leaveDays}
							label="Number of Days"
							required={false}
							helperText="Number of days"
							onChange={(value) => {}}
							isError={false}
							disabled={true}
						/>
					</Col>

					<Col span={12} className={`${styles['col']} margin-top-10`}>
						<InputField
							name="maxLeaves"
							value={
								maxLeaves - leaveDays >= 0
									? maxLeaves - leaveDays
									: 0
							}
							label="Remaining Leaves"
							required={false}
							helperText="Total Leaves"
							onChange={(value) => {}}
							isError={false}
							disabled={true}
						/>
					</Col>
					<Col span={24} className={`${styles['col']} margin-top-10`}>
						<TextareaFieldInput
							name="reason"
							value={leaveRequestDetails.reason}
							label="Reason"
							required={true}
							helperText="Reason required"
							onChange={(value) =>
								handleChange(value, 'reason', true)
							}
							isError={formError.reason}
						/>
					</Col>
				</Row>
				{attachmentRequired && (
					<div className={styles['modalUploadContainer']}>
						<label className={styles['modalUploadLabel']}>
							{'Attachment'}
							{!edit && attachmentRequired && (
								<span className={styles['requiredAsterisk']}>
									*
								</span>
							)}
						</label>
						<Form.Item required>{CustomDragger}</Form.Item>
					</div>
				)}
			</div>
			<div className={styles['buttons']}>
				<Buttons buttons={myButtons} />
			</div>
		</Modal>
	);
};

export default AddLeaveRequestModal;
